import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Download, RefreshCw, ChevronRight, Handshake, Eye, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from './AdminHeader';
import { listPartners, partnerStats, exportPartnersUrl, hidePartner, restorePartner, deletePartner } from '../lib/marketingApi';
import { getToken } from '../lib/auth';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ViewTabs, ModerationMenu } from './ModerationControls';

export const PARTNER_STATUSES = [
  { value: 'new', label: 'New', cls: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-amber-100 text-amber-700' },
  { value: 'in_discussion', label: 'In Discussion', cls: 'bg-violet-100 text-violet-700' },
  { value: 'agreement_sent', label: 'Agreement Sent', cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'active_partner', label: 'Active Partner', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'declined', label: 'Declined', cls: 'bg-rose-100 text-rose-700' },
];
export const partnerStatusMeta = (v) => PARTNER_STATUSES.find((s) => s.value === v) || PARTNER_STATUSES[0];
export const INTEREST_LABEL = {
  bill_collection: 'Bill Collection', data_extraction: 'Data Extraction', ubm_payments: 'UBM Payments',
  ubm_services: 'UBM Services', ubm_migration: 'UBM Migration', platform_as_a_service: 'Platform as a Service', other: 'Other',
};

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

export default function PartnersCRM() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('active');
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, res] = await Promise.all([
        partnerStats(),
        listPartners({ status: status || undefined, search: search || undefined, view, page, limit }),
      ]);
      setStats(s);
      setTotal(res.total);
      setItems((prev) => (page === 1 ? res.items : [...prev, ...res.items]));
    } catch (e) {
      toast.error('Failed to load partners.');
    } finally {
      setLoading(false);
    }
  }, [page, status, search, view]);

  useEffect(() => { load(); }, [page]);
  useEffect(() => { setPage(1); load(); }, [status, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const reload = () => { setPage(1); load(); };
  const doHide = async (id, reason) => { try { await hidePartner(id, reason); toast.success(reason === 'spam' ? 'Marked as spam' : 'Moved to trash'); reload(); } catch (e) { toast.error('Action failed'); } };
  const doRestore = async (id) => { try { await restorePartner(id); toast.success('Restored'); reload(); } catch (e) { toast.error('Restore failed'); } };
  const doPurge = async (id) => { if (!window.confirm('Permanently delete this partner application? This cannot be undone.')) return; try { await deletePartner(id); toast.success('Permanently deleted'); reload(); } catch (e) { toast.error('Delete failed'); } };

  const doExport = async () => {
    try {
      const r = await fetch(exportPartnersUrl(), { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error('export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'billportal_partners.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partners-list">
      <AdminHeader
        active="partners"
        actions={(
          <>
            <button onClick={doExport} data-testid="partners-export-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#002B87]"><Download className="h-4 w-4" /> Export CSV</button>
            <button onClick={() => { setPage(1); load(); }} data-testid="partners-refresh-btn" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          </>
        )}
      />

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Partner applications</h1>
          <p className="mt-0.5 text-sm text-slate-500">Companies that want to use BillPortal&rsquo;s technology or services for their customers</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="partners-stats">
            <StatCard label="All" value={stats.total} active={!status} onClick={() => setStatus('')} />
            {PARTNER_STATUSES.map((s) => (
              <StatCard key={s.value} label={s.label} value={stats.by_status?.[s.value] || 0} active={status === s.value} onClick={() => setStatus(status === s.value ? '' : s.value)} />
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <ViewTabs view={view} setView={setView} counts={{ spam: stats?.hidden_spam, deleted: stats?.hidden_deleted }} testidPrefix="partners" />
          <form onSubmit={onSearch} className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="partners-search" placeholder="Search name, email, company or website…" className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-900 outline-none focus:border-[#0035A8]" />
          </form>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Company</div>
            <div className="col-span-2">Interested in</div>
            <div className="col-span-2">Assigned to</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Last contacted</div>
          </div>
          {items.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
              <Handshake className="h-8 w-8" />
              <p className="text-sm">No partner applications yet. They&rsquo;ll appear here as companies apply.</p>
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} role="button" tabIndex={0} onClick={() => navigate(`/admin/partners/${p.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/partners/${p.id}`); }} data-testid={`partner-row-${p.id}`} className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3.5 text-left transition-colors hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
              <div className="col-span-3 min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                <div className="truncate text-xs text-slate-500">{p.email}</div>
              </div>
              <div className="col-span-2 truncate text-sm text-slate-700">{p.company || <span className="text-slate-300">—</span>}</div>
              <div className="col-span-2 min-w-0">
                <div className="flex flex-wrap gap-1">
                  {(p.interests || []).slice(0, 2).map((i) => (
                    <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{INTEREST_LABEL[i] || i}</span>
                  ))}
                  {(p.interests || []).length > 2 && <span className="text-[10px] text-slate-400">+{p.interests.length - 2}</span>}
                  {(p.interests || []).length === 0 && <span className="text-slate-300">—</span>}
                </div>
              </div>
              <div className="col-span-2 min-w-0 truncate text-sm">
                {p.owner ? <span className="inline-flex items-center gap-1 text-slate-700"><UserIcon className="h-3 w-3 text-slate-400" />{p.owner}</span> : <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Unassigned</span>}
              </div>
              <div className="col-span-1"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${partnerStatusMeta(p.status).cls}`}>{partnerStatusMeta(p.status).label}</span></div>
              <div className="col-span-2 flex items-center justify-end gap-1 text-xs text-slate-500">
                <span className="hidden lg:inline">{p.last_contacted_at || p.booking_link_sent_at ? fmt(p.last_contacted_at || p.booking_link_sent_at) : <span className="text-slate-300">Not contacted</span>}</span>
                <ModerationMenu view={view} isAdmin={isAdmin} testid={`partner-${p.id}`} onSpam={() => doHide(p.id, 'spam')} onDelete={() => doHide(p.id, 'deleted')} onRestore={() => doRestore(p.id)} onPurge={() => doPurge(p.id)} />
              </div>
            </div>
          ))}
          {items.length < total && (
            <div className="p-4 text-center">
              <button onClick={() => setPage((pg) => pg + 1)} disabled={loading} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {loading ? 'Loading…' : `Load more (${total - items.length} left)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, active, onClick }) {
  return (
    <button onClick={onClick} className={`rounded-lg border bg-white p-3 text-left transition-all ${active ? 'border-[#0035A8] ring-1 ring-[#0035A8]' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="text-xl font-bold text-slate-900">{value}</div>
      <div className="mt-0.5 truncate text-[11px] text-slate-500">{label}</div>
    </button>
  );
}
